import { ShieldCheck, Stethoscope } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

import { EmptyState } from '../../components/empty-state'
import { Badge } from '../../components/ui/badge'
import { Card, CardContent, CardHeader } from '../../components/ui/card'
import { Skeleton } from '../../components/ui/skeleton'
import { useFormatters } from '../../i18n/format'
import type { CapabilityProfile } from '../vehicles/api-types'
import { CanUnavailableNotice } from '../vehicles/capability-notice'
import type { VehicleDtcView } from './api-types'
import { DtcList } from './dtc-list'
import { summariseDtc } from './dtc-model'
import { vehicleDiagnosticsPath } from './routes'

/**
 * Active fault codes for one vehicle, as the vehicle detail page shows them.
 *
 * Four different empty states, because they are four different facts:
 *
 *   never_reported     this vehicle does not report fault codes at all — no CAN
 *                      adapter, or a program number that does not publish them. Shows
 *                      the CAN sentence with the program number.
 *   stopped_reporting  it used to. The date is the finding.
 *   reporting, none    the vehicle reports fault codes and currently has none. This is
 *                      the ONLY one that is good news, and it is the only one drawn in
 *                      green.
 *   loading            a skeleton, not an empty list.
 *
 * Collapsing them into "aucun défaut" would tell a manager that a truck with no OBD
 * connection is healthy.
 */
export function VehicleDtcPanel({
  vehicleId,
  dtc,
  capabilities,
  isLoading = false,
  className,
}: {
  vehicleId: string
  dtc?: VehicleDtcView
  capabilities?: CapabilityProfile
  isLoading?: boolean
  className?: string
}) {
  const { t } = useTranslation()
  const formatters = useFormatters()

  const active = dtc?.active ?? []
  const summary = summariseDtc(active)
  const channelStatus = dtc?.channelStatus ?? null

  return (
    <Card className={className}>
      <CardHeader
        title={t('diagnostics:panel.title')}
        description={
          dtc?.lastReportedAt
            ? t('diagnostics:panel.lastReportedAt', {
                when: formatters.dateTime(dtc.lastReportedAt),
              })
            : t('diagnostics:panel.subtitle')
        }
        action={
          <div className="flex items-center gap-2">
            {summary.total > 0 ? (
              <Badge tone={summary.worst === 'critical' ? 'danger' : 'warning'}>
                {t('diagnostics:panel.activeCount', { count: summary.total })}
              </Badge>
            ) : null}
            <Link
              to={vehicleDiagnosticsPath(vehicleId)}
              className="text-xs font-medium text-accent-text hover:underline"
            >
              {t('diagnostics:panel.openFull')}
            </Link>
          </div>
        }
      />

      <CardContent className="space-y-3">
        {isLoading ? (
          <>
            <Skeleton className="h-4 w-48" />
            <Skeleton className="h-4 w-full" />
          </>
        ) : active.length > 0 ? (
          <>
            <DtcList events={active} />
            {/* The count the device itself reports (AVL id 30) may legitimately exceed
                the list: id 281 truncates at 128 bytes. Saying so beats a manager
                counting four rows under a badge that says nine. */}
            {dtc?.reportedCount != null && dtc.reportedCount > active.length ? (
              <p className="text-xs text-foreground-subtle">
                {t('diagnostics:panel.truncated', {
                  reported: dtc.reportedCount,
                  listed: active.length,
                })}
              </p>
            ) : null}
            {summary.unknownDescriptions > 0 ? (
              <p className="text-xs text-foreground-subtle">
                {t('diagnostics:panel.unknownDescriptions', {
                  count: summary.unknownDescriptions,
                })}
              </p>
            ) : null}
          </>
        ) : channelStatus === 'never_reported' || channelStatus === null ? (
          <CanUnavailableNotice profile={capabilities} />
        ) : channelStatus === 'stopped_reporting' ? (
          <EmptyState
            compact
            icon={Stethoscope}
            title={t('diagnostics:panel.stopped.title')}
            description={
              dtc?.lastReportedAt
                ? t('diagnostics:panel.stopped.description', {
                    when: formatters.dateTime(dtc.lastReportedAt),
                  })
                : t('diagnostics:panel.stopped.descriptionUndated')
            }
          />
        ) : (
          <EmptyState
            compact
            icon={ShieldCheck}
            title={t('diagnostics:panel.clean.title')}
            description={t('diagnostics:panel.clean.description')}
          />
        )}
      </CardContent>
    </Card>
  )
}
